import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  forwardRef,
  inject,
  input,
  model,
  signal,
} from '@angular/core';
import { NG_VALUE_ACCESSOR, type ControlValueAccessor } from '@angular/forms';

import type { EsnDropdownOption } from '../../../shared/types/forms';
import { IconComponent } from '../../atoms/icon/icon.component';

let nextId = 0;

/**
 * Labelled select for console forms: a 56px trigger that opens a list of options, fully
 * keyboard-operable. Works with `[(value)]`, `[(ngModel)]` and reactive forms.
 */
@Component({
  selector: 'esn-dropdown-field',
  imports: [IconComponent],
  templateUrl: './dropdown-field.component.html',
  styleUrl: './dropdown-field.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => DropdownFieldComponent), multi: true }],
  host: { '(document:click)': 'handleDocumentClick($event)' },
})
export class DropdownFieldComponent implements ControlValueAccessor {
  readonly value = model<string | null>(null);
  readonly options = input<EsnDropdownOption[]>([]);
  readonly label = input('');
  readonly placeholder = input('');
  readonly disabled = input(false);

  protected readonly id = `esn-dropdown-field-${nextId++}`;
  protected readonly open = signal(false);
  protected readonly activeIndex = signal(-1);
  private readonly formDisabled = signal(false);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  protected readonly isDisabled = computed(() => this.disabled() || this.formDisabled());
  protected readonly selected = computed(() => this.options().find((o) => o.value === this.value()) ?? null);
  protected readonly activeId = computed(() =>
    this.open() && this.activeIndex() >= 0 ? `${this.id}-option-${this.activeIndex()}` : null);

  private onChange: (value: string | null) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  protected toggle(): void {
    if (this.open()) this.close();
    else this.show();
  }

  protected handleKeydown(event: KeyboardEvent): void {
    if (this.isDisabled()) return;

    if (!this.open()) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
        event.preventDefault();
        this.show();
      }
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.move(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.move(-1);
        break;
      case 'Home':
        event.preventDefault();
        this.activeIndex.set(this.firstEnabled(0, 1));
        break;
      case 'End':
        event.preventDefault();
        this.activeIndex.set(this.firstEnabled(this.options().length - 1, -1));
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.choose(this.activeIndex());
        break;
      case 'Escape':
        event.preventDefault();
        this.close();
        break;
      case 'Tab':
        this.close();
        break;
    }
  }

  protected choose(index: number): void {
    const option = this.options()[index];
    if (!option || option.disabled) return;
    this.value.set(option.value);
    this.onChange(option.value);
    this.close();
  }

  protected handleDocumentClick(event: MouseEvent): void {
    if (this.open() && !this.host.nativeElement.contains(event.target as Node)) this.close();
  }

  protected handleBlur(): void {
    this.onTouched();
  }

  writeValue(value: string | null): void {
    this.value.set(value ?? null);
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.formDisabled.set(disabled);
    if (disabled) this.close();
  }

  private show(): void {
    if (this.isDisabled()) return;
    const current = this.options().findIndex((o) => o.value === this.value());
    this.activeIndex.set(current >= 0 ? current : this.firstEnabled(0, 1));
    this.open.set(true);
  }

  private close(): void {
    this.open.set(false);
    this.activeIndex.set(-1);
  }

  private move(step: 1 | -1): void {
    const count = this.options().length;
    if (!count) return;
    const start = this.activeIndex() < 0 ? (step === 1 ? 0 : count - 1) : this.activeIndex() + step;
    const next = this.firstEnabled(start, step);
    if (next >= 0) this.activeIndex.set(next);
  }

  /**
   * First enabled option from `start` walking by `step`, or -1 when none is left.
   */
  private firstEnabled(start: number, step: 1 | -1): number {
    const options = this.options();
    for (let i = start; i >= 0 && i < options.length; i += step) {
      if (!options[i].disabled) return i;
    }
    return -1;
  }
}
